import { types } from 'mobx-state-tree';

import { OrderOriginTypeType } from '../../types/order/OrderOriginType';
import { OrderStatusType } from '../../types/order/OrderStatusClass';
import OrderTotalClass from '../../types/order/OrderTotalClass';
import { OrderTypeType } from '../../types/order/OrderType';
import { HiddenProductTypes } from '../../types/ProductTypeClass';
import GdprData from '../analytics/GdprInfo';
import Price from '../Price';
import OrderAlerts from './OrderAlerts';
import OrderPayment from './OrderPayment';
import OrderProduct from './OrderProduct';
import OrderShipping from './OrderShipping';
import OrderStatusHistoryEntry from './OrderStatusHistoryEntry';
import OrderTotalValue from './OrderTotalValue';

const Order = types
  .compose(
    OrderAlerts,
    types.model('Order', {
      confirmation_email: types.maybeNull(types.string),
      currency: types.maybeNull(types.string),
      currency_value: types.maybeNull(types.integer),
      date_purchased: types.maybeNull(types.string),
      gdpr_info: types.maybeNull(GdprData),
      has_licensed_products: types.boolean,
      header_content: types.maybeNull(types.string),
      html_widget: types.maybeNull(types.string),
      id: types.identifierNumber,
      is_success_seen: types.boolean,
      last_modified: types.maybeNull(types.string),
      last_status: types.maybeNull(OrderStatusType),
      no_taxes: types.maybeNull(types.boolean),
      number_of_products: types.number,
      origin_type: types.maybeNull(OrderOriginTypeType),
      payment: types.maybeNull(OrderPayment),
      payment_status: types.maybeNull(OrderStatusType),
      products: types.array(OrderProduct),
      products_total_price: types.maybeNull(Price),
      receipt_url: types.maybeNull(types.string),
      seller_name: types.maybeNull(types.string),
      shipping: types.maybeNull(OrderShipping),
      shipping_price: types.maybeNull(Price),
      show_product_code: types.boolean,
      status_history: types.optional(types.array(OrderStatusHistoryEntry), []),
      total_price: types.maybeNull(Price),
      total_tax: types.maybeNull(types.number),
      totals: types.maybeNull(types.map(OrderTotalValue)),
      tracking_code: types.maybeNull(types.string),
      tracking_pixels: types.array(types.string),
      tracking_url: types.maybeNull(types.string),
      type: types.maybeNull(OrderTypeType),
      weight: types.maybeNull(types.string),
      your_reference: types.maybeNull(types.string),
    })
  )
  .named('Order')
  .views((self) => {
    return {
      get analytics() {
        /**
         * 16/07/2025
         * Report values taxless for analytics
         */
        const shippingPrice = self.shipping_price?.getPrice(false) || null;
        const taxAmount = self.total_tax;
        const transactionValue =
          self.products_total_price?.getPrice(false) || null;

        return {
          shipping: shippingPrice,
          tax: taxAmount,
          value: transactionValue,
        };
      },
      get allTotals() {
        return self.noTotals ? [] : [...self.totals.values()];
      },
      get customerInfo() {
        return self.payment?.address ? self.payment.address : null;
      },
      get hasProducts() {
        return self.products?.length > 0 || false;
      },
      get paymentMethod() {
        return self.payment && self.payment.method;
      },
      get pickupPoint() {
        return self.shipping && self.shipping.pick_up_point;
      },
      get productsTotal() {
        return self.noTotals
          ? null
          : self.totals.get(OrderTotalClass.PRODUCTS_TOTAL);
      },
      get noTotals() {
        return self.totals === null;
      },
      get orderReference() {
        return self.your_reference;
      },
      get shippingAddress() {
        return self.shipping?.address;
      },
      get shippingMethod() {
        return self.shipping && self.shipping.method;
      },
      get shippingTotal() {
        return self.noTotals
          ? null
          : self.totals.get(OrderTotalClass.SHIPPING_TOTAL);
      },
      get taxFreeTotal() {
        return self.noTotals
          ? null
          : self.totals.get(OrderTotalClass.TAX_FREE_TOTAL);
      },
      get taxTotal() {
        return self.noTotals
          ? null
          : self.totals.get(OrderTotalClass.TAX_TOTAL);
      },
      get totalPrice() {
        return self.noTotals ? null : self.totals?.get(OrderTotalClass.TOTAL);
      },
      get currentOrderProducts() {
        const filter = new Set(Object.values(HiddenProductTypes.currentOrder));
        const visibleProducts = (product) => !filter.has(product.type);
        return self.products.filter(visibleProducts);
      },
    };
  });

export default Order;
